import { FocusEvent, KeyboardEvent, forwardRef, useEffect, useMemo, useRef } from 'react';

import { throttle } from '@eneco/common/fn';
import {
  Combobox as AriaCombobox,
  ComboboxItem,
  ComboboxPopover,
  useComboboxState,
  ComboboxCancel,
} from 'ariakit/combobox';
import parse, { DOMNode, domToReact } from 'html-react-parser';

import {
  backgroundStyle,
  cancelButtonStyle,
  comboboxItemStyle,
  comboboxWrapperBaseStyle,
  inputStyle,
  inputWrapperRecipe,
  noResultsStyle,
  popoverStyle,
  positionStyle,
} from './InputCombobox.css';
import { useInputIds } from '../../hooks/useInputIds';
import { useValidationErrorEvent } from '../../hooks/useValidationErrorEvent';
import { CloseIcon } from '../../icons/eneco';
import { useI18nTranslations } from '../../providers/i18n';
import { InputBaseProps } from '../Input/Input';
import { InputError } from '../Input/InputError';
import { Hint } from '../Input/InputHint';
import { Label } from '../Input/InputLabel';
import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';

type Props = InputBaseProps & {
  defaultValue?: string;
  options?: string[];
  filterMode?: 'external' | 'internal';
  onChange?: (value: string) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  /** Set to false to keep the popover below the input instead of flipping upwards when space is limited (e.g. behind a mobile keyboard). */
  flip?: boolean;
};

const replace = (node: DOMNode) => {
  if (node && 'name' in node && node.name === 'strong') {
    const children = 'children' in node && node.children.length ? domToReact(node.children as DOMNode[]) : undefined;
    return <Text weight="bold">{children}</Text>;
  }
};

const parseText = (augmentedText: string) => <>{parse(augmentedText, { replace })}</>;

export const InputCombobox = forwardRef<HTMLInputElement, Props>(
  ({ filterMode = 'external', onChange, onBlur, options = [], defaultValue, flip = true, ...inputProps }, ref) => {
    const wrapperRef = useRef<HTMLInputElement>(null);

    const overflowPadding = 8;
    const useInternalFilter = filterMode !== 'external';
    const combobox = useComboboxState({
      gutter: 0,
      sameWidth: true,
      list: useInternalFilter ? options : undefined,
      defaultValue,
      overflowPadding,
      flip,
      limit: 50,
    });
    const { error, hint, label, isDisabled, isOptional, name, placeholder } = inputProps;

    const { inputId, describedBy, errorId, hintId } = useInputIds({ error, hint });
    const { clearInput, closePopup, noResults } = useI18nTranslations();

    useEffect(() => {
      onChange?.(combobox.value);
    }, [combobox.value, onChange]);

    const renderMatches = useMemo(
      () => (useInternalFilter ? combobox.matches.length && combobox.matches : options.length && options),
      [combobox.matches, options, useInternalFilter],
    );

    const closeDropdown = () => {
      combobox.setOpen(false);
      combobox.anchorRef.current?.focus();
    };

    const keyDownHandler = (e: KeyboardEvent<HTMLButtonElement>) => {
      if (['Enter', ' ', 'Escape'].includes(e.key)) {
        e.preventDefault();
        closeDropdown();
      }
    };

    const filterValues = combobox.value
      .toLowerCase()
      .split(' ')
      .filter(Boolean)
      .sort((a, b) => a.length - b.length);

    const highlightMatch = (match: string) => {
      if (combobox.value === '') {
        return <Text>{match}</Text>;
      }
      const augmentedText = match
        .split(' ')
        .map(word => {
          const firstMatch = filterValues.find(el => word.toLowerCase().includes(el));
          return firstMatch ? word.replace(new RegExp(firstMatch, 'i'), `<strong>$&</strong>`) : word;
        })
        .join(' ');
      return <Text>{parseText(augmentedText)}</Text>;
    };

    useEffect(() => {
      if (!combobox.popoverRef.current) return;

      const setPopoverHeight = throttle(() => {
        if (!combobox.popoverRef.current || !wrapperRef.current) return;
        const topPopover = combobox.popoverRef.current.style.transform.includes('px, -');

        // Ariakit exposes a --popover-available-height CSS custom property
        // (https://github.com/ariakit/ariakit/blob/main/packages/ariakit/src/popover/popover-state.ts), but as InputCombobox
        // has a max-height set, we make sure the combobox actually matches the calculated available height
        //
        // A better solution would be to style the input and popover accordingly, but with the amount of borders and outlines
        // involved, this lead to a solution way more complex, style-wise.
        const popoverHeight = combobox.popoverRef.current.clientHeight || 0;
        wrapperRef.current.style.setProperty('--combobox-popover-height', `${popoverHeight}px`);

        wrapperRef.current.style.setProperty('--combobox-background-bottom', topPopover ? '0' : 'auto');
        wrapperRef.current.style.setProperty('--combobox-background-top', topPopover ? 'auto' : '0');

        wrapperRef.current.style.setProperty('--combobox-divider-bottom', topPopover ? 'auto' : '0');
        wrapperRef.current.style.setProperty('--combobox-divider-top', topPopover ? '0' : 'auto');
      }, 17); // 17ms ~= 60fps

      const popoverMutationObserver = new MutationObserver(setPopoverHeight);
      popoverMutationObserver.observe(combobox.popoverRef.current, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['style'],
      });
    }, [combobox.popoverRef]);

    useValidationErrorEvent({ error, name }, wrapperRef);

    return (
      <div className={positionStyle}>
        <Stack gap="2">
          <Label htmlFor={inputId} isOptional={isOptional}>
            {label}
          </Label>
          <Stack.Item grow>
            <div
              className={comboboxWrapperBaseStyle}
              data-combobox-open={String(combobox.open)}
              data-combobox-invalid={String(!!error)}
              data-combobox-disabled={String(!!isDisabled)}
              ref={wrapperRef}>
              <div className={inputWrapperRecipe({ isOpen: combobox.open })}>
                <AriaCombobox
                  state={combobox}
                  placeholder={placeholder}
                  onBlur={event => onBlur?.(event)}
                  disabled={isDisabled}
                  id={inputId}
                  aria-describedby={describedBy}
                  aria-invalid={error ? true : undefined}
                  aria-required={!isOptional}
                  ref={ref}
                  className={inputStyle}
                />
              </div>
              <div className={backgroundStyle} />
              <ComboboxPopover state={combobox} className={popoverStyle}>
                {(renderMatches || []).map(value => (
                  <ComboboxItem key={value} value={value} className={comboboxItemStyle}>
                    {highlightMatch(value)}
                  </ComboboxItem>
                ))}
                {!renderMatches && combobox.value ? <div className={noResultsStyle}>{noResults}</div> : ''}
              </ComboboxPopover>
              {combobox.open && (
                <ComboboxCancel
                  state={combobox}
                  onKeyDown={keyDownHandler}
                  onClick={closeDropdown}
                  aria-label={combobox.activeId ? closePopup : clearInput}
                  className={cancelButtonStyle}>
                  <CloseIcon size="medium" />
                </ComboboxCancel>
              )}
            </div>
            {error ? <InputError id={errorId}>{error}</InputError> : null}
          </Stack.Item>
          {hint ? <Hint id={hintId}>{hint}</Hint> : null}
        </Stack>
      </div>
    );
  },
);
